import { Injectable, OnDestroy } from '@angular/core';
import { Subject } from 'rxjs';
import { CVANotificationService } from '../cva-notification/cva-notification.service';
import { NotificationClassEnum } from 'app/models/notification-class.enum';

export type AudioSourceType = 'transcribe' | 'telehealth';

export interface AudioStreamState {
  audioSource: AudioSourceType;
  teleHealthStream: MediaStream | null;
  teleHealthAudioStream: MediaStream | null;
  microphoneStream: MediaStream | null;
}

@Injectable({
  providedIn: 'root'
})
export class AudioStreamService implements OnDestroy {
  private _audioSource: AudioSourceType = 'transcribe';
  private _teleHealthStream: MediaStream | null = null;
  private _teleHealthAudioStream: MediaStream | null = null;
  private _microphoneStream: MediaStream | null = null;
  private _audioContext: AudioContext | null = null;
  private _isCleaningUp = false;

  // Event emitter for when screen share ends
  private readonly _screenShareEnded$ = new Subject<void>();
  readonly screenShareEnded$ = this._screenShareEnded$.asObservable();

  // Error message templates (DRY principle)
  private readonly ERROR_MESSAGES = {
    microphone: {
      NotAllowedError: 'Microphone access denied. Please allow microphone permissions in your browser settings and try again.',
      NotFoundError: 'No microphone found. Please connect a microphone and refresh the page.',
      NotReadableError: 'Microphone is already in use by another application. Please close other apps using the microphone and try again.',
      OverconstrainedError: 'Your microphone does not meet the required specifications. Please try a different microphone.',
      SecurityError: 'Microphone access blocked for security reasons. Please check your browser security settings.',
      AbortError: 'Microphone access was aborted. Please try again.'
    },
    displayMedia: {
      NotAllowedError: 'Screen sharing was cancelled or denied. Please click "Share" and ensure "Share audio" is checked.',
      NotFoundError: 'No screen or window available to share. Please try again.',
      NotReadableError: 'Unable to capture screen. Please close other apps that might be using screen capture and try again.',
      AbortError: 'Screen sharing was cancelled. Please try again if you want to use Telehealth mode.',
      InvalidStateError: 'Screen sharing is in an invalid state. Please refresh the page and try again.'
    }
  };

  constructor(
    private readonly cvaNotificationService: CVANotificationService
  ) { }

  get audioSource(): AudioSourceType {
    return this._audioSource;
  }

  get teleHealthStream(): MediaStream | null {
    return this._teleHealthStream;
  }

  get teleHealthAudioStream(): MediaStream | null {
    return this._teleHealthAudioStream;
  }

  get microphoneStream(): MediaStream | null {
    return this._microphoneStream;
  }

  /**
   * Check if there is active telehealth audio stream with audio tracks
   */
  get hasActiveTelehealthAudio(): boolean {
    if (!this._teleHealthAudioStream) return false;
    const audioTracks = this._teleHealthAudioStream.getAudioTracks();
    return audioTracks.length > 0 && audioTracks[0].readyState === 'live';
  }

  /**
   * Get the current state of audio streams
   */
  getState(): AudioStreamState {
    return {
      audioSource: this._audioSource,
      teleHealthStream: this._teleHealthStream,
      teleHealthAudioStream: this._teleHealthAudioStream,
      microphoneStream: this._microphoneStream
    };
  }

  /**
   * Set audio source type
   */
  setAudioSource(source: AudioSourceType): void {
    this._audioSource = source;
  }

  /**
   * Initialize transcribe mode (microphone only)
   */
  async initializeTranscribeMode(): Promise<MediaStream | null> {
    try {
      // Clean up any existing telehealth streams
      this.cleanupTelehealthStreams();
      
      // Check if getUserMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Your browser does not support microphone access. Please use a modern browser like Chrome or Edge.');
      }
      
      // Force explicit microphone access instead of using default
      const micStream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        } 
      });
      
      // Verify we got audio tracks
      const audioTracks = micStream.getAudioTracks();
      if (audioTracks.length === 0) {
        throw new Error('No microphone detected. Please connect a microphone and try again.');
      }
      
      this.cvaNotificationService.showNotification(
        'Audio source set to Transcribe (microphone).',
        NotificationClassEnum.SUCCESS
      );
      
      return micStream;
    } catch (e) {
      const errorMessage = this.getMicrophoneErrorMessage(e);
      this.cvaNotificationService.showNotification(
        errorMessage,
        NotificationClassEnum.ERROR
      );
      return null;
    }
  }

  /**
   * Initialize telehealth mode (screen share + microphone)
   */
  async initializeTelehealthMode(): Promise<MediaStream | null> {
    try {
      // Check if getDisplayMedia is supported
      if (!navigator.mediaDevices || !navigator.mediaDevices.getDisplayMedia) {
        throw new Error('Your browser does not support screen sharing. Please use Chrome or Edge.');
      }

      const screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: true
      });

      // Verify screen audio track exists
      const screenAudioTracks = screenStream.getAudioTracks();
      if (screenAudioTracks.length === 0) {
        screenStream.getTracks().forEach(track => track.stop());
        throw new Error('No audio track found in screen share. Please ensure "Share audio" is checked when sharing.');
      }

      this._teleHealthStream = screenStream;

      // Get microphone stream with error handling
      try {
        this._microphoneStream = await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (micError) {
        // Continue without microphone - screen audio is enough
        console.warn('Failed to access microphone for Telehealth mode, continuing with screen audio only.', micError);
        this._microphoneStream = null;
      }

      // Create mixed mono stream
      const mixedMonoStream = await this.createMixedMonoStream(
        screenStream,
        this._microphoneStream
      );

      // Setup screen share end handler
      this.setupScreenShareEndHandler(screenStream);

      this._teleHealthAudioStream = mixedMonoStream;

      const successMessage = this._microphoneStream 
        ? 'Audio source set to Telehealth (screen share + microphone).'
        : 'Audio source set to Telehealth (screen share only).';
      
      this.cvaNotificationService.showNotification(
        successMessage,
        NotificationClassEnum.SUCCESS
      );

      return mixedMonoStream;
    } catch (e) {
      const errorMessage = this.getDisplayMediaErrorMessage(e);
      this.cvaNotificationService.showNotification(
        errorMessage,
        NotificationClassEnum.ERROR
      );
      
      // Cleanup on error
      this.cleanupAllStreams();
      // this._audioSource = 'transcribe';
      
      return null;
    }
  }

  /**
   * Create a mixed mono audio stream from screen and microphone
   */
  async createMixedMonoStream(
    screenStream: MediaStream,
    micStream?: MediaStream | null
  ): Promise<MediaStream> {
    try {
      // Reuse or create audio context
      if (!this._audioContext || this._audioContext.state === 'closed') {
        this._audioContext = new AudioContext();
      }

      const audioContext = this._audioContext;
      
      // Resume audio context if suspended
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }
      
      const destination = audioContext.createMediaStreamDestination();

      // Screen audio
      const screenTrack = screenStream.getAudioTracks()[0];
      if (!screenTrack) {
        throw new Error('No screen audio track found. Please ensure "Share audio" is enabled.');
      }

      // Verify track is active
      if (screenTrack.readyState !== 'live') {
        throw new Error('Screen audio track is not active. Please try sharing your screen again.');
      }

      const screenSource = audioContext.createMediaStreamSource(
        new MediaStream([screenTrack])
      );

      // Force mono
      const screenMerger = audioContext.createChannelMerger(1);
      screenSource.connect(screenMerger);
      screenMerger.connect(destination);

      // Mic audio (optional)
      if (micStream) {
        const micTrack = micStream.getAudioTracks()[0];
        if (micTrack && micTrack.readyState === 'live') {
          try {
            const micSource = audioContext.createMediaStreamSource(
              new MediaStream([micTrack])
            );

            const micMerger = audioContext.createChannelMerger(1);
            micSource.connect(micMerger);
            micMerger.connect(destination);
          } catch (micError) {
            // Continue without microphone if mixing fails
            console.warn('Failed to mix microphone audio, continuing with screen audio only.', micError);
          }
        }
      }

      // destination.stream contains ONE mono mixed audio track
      return destination.stream;
    } catch (error) {
      throw new Error('Failed to mix audio streams: ' + this.extractErrorMessage(error));
    }
  }

  /**
   * Update microphone device during telehealth recording (keeps screen share active)
   */
  async updateTelehealthMicrophoneDevice(deviceId: string): Promise<MediaStream | null> {
    try {
      // Only allow this during active telehealth mode
      if (this._audioSource !== 'telehealth' || !this._teleHealthStream) {
        throw new Error('Not in active Telehealth mode');
      }

      // Stop previous microphone stream if exists
      if (this._microphoneStream) {
        this.stopStream(this._microphoneStream);
        this._microphoneStream = null;
      }

      // Get new microphone stream with specified device
      try {
        this._microphoneStream = await navigator.mediaDevices.getUserMedia({
          audio: { deviceId }
        });
      } catch (micError) {
        const errorMessage = this.getMicrophoneErrorMessage(micError);
        this.cvaNotificationService.showNotification(
          errorMessage,
          NotificationClassEnum.ERROR
        );
        // Continue without microphone - screen audio is enough
        this._microphoneStream = null;
      }

      // Recreate mixed mono stream with new microphone
      const newMixedStream = await this.createMixedMonoStream(
        this._teleHealthStream,
        this._microphoneStream
      );

      // Update the stored telehealth audio stream
      if (this._teleHealthAudioStream) {
        this.stopStream(this._teleHealthAudioStream);
      }
      this._teleHealthAudioStream = newMixedStream;

      return newMixedStream;
    } catch (error) {
      this.cvaNotificationService.showNotification(
        'Failed to update microphone device: ' + this.extractErrorMessage(error),
        NotificationClassEnum.ERROR
      );
      return null;
    }
  }

  /**
   * Setup handler for when screen share ends (preserves existing handlers to prevent race conditions)
   */
  private setupScreenShareEndHandler(screenStream: MediaStream): void {
    const videoTrack = screenStream.getVideoTracks()[0];
    if (!videoTrack) return;

    // Preserve existing handler chain to avoid race conditions
    const originalOnEnded = videoTrack.onended;
    videoTrack.onended = () => {
      if (originalOnEnded) originalOnEnded.call(videoTrack);
      
      // Emit event to notify listeners that screen share has ended
      this._screenShareEnded$.next();
      
      this.cleanupAllStreams();
    };
  }

  /**
   * Stop all tracks in a stream efficiently
   */
  private stopStream(stream: MediaStream | null): void {
    if (!stream) return;
    const tracks = stream.getTracks();
    for (let i = 0; i < tracks.length; i++) {
      tracks[i].stop();
    }
  }

  /**
   * Clean up telehealth-specific streams (optimized for efficiency)
   */
  private cleanupTelehealthStreams(): void {
    this.stopStream(this._teleHealthStream);
    this._teleHealthStream = null;
    
    this.stopStream(this._teleHealthAudioStream);
    this._teleHealthAudioStream = null;
    
    this.stopStream(this._microphoneStream);
    this._microphoneStream = null;
  }

  /**
   * Clean up all audio streams and audio context (non-blocking with guard against multiple calls)
   */
  cleanupAllStreams(): void {
    // Prevent concurrent cleanup calls (race condition fix)
    if (this._isCleaningUp) return;
    this._isCleaningUp = true;

    this.cleanupTelehealthStreams();

    if (this._audioContext && this._audioContext.state !== 'closed') {
      this._audioContext.close()
        .then(() => {
          this._audioContext = null;
        })
        .catch(() => {
          this._audioContext = null;
        })
        .finally(() => {
          this._isCleaningUp = false;
        });
    } else {
      this._isCleaningUp = false;
    }
  }

  /**
   * Stop all active streams
   */
  stopAllStreams(): void {
    this.cleanupAllStreams();
  }

  /**
   * Get screen share ended callback
   */
  getScreenShareEndedCallback(): () => void {
    return () => {
      this.cleanupAllStreams();
    };
  }

  /**
   * Get specific error message for microphone access failures (DRY implementation)
   */
  private getMicrophoneErrorMessage(e: unknown): string {
    if (e instanceof DOMException && e.name in this.ERROR_MESSAGES.microphone) {
      return this.ERROR_MESSAGES.microphone[e.name as keyof typeof this.ERROR_MESSAGES.microphone];
    }
    
    const errorMessage = this.extractErrorMessage(e);
    return (e instanceof DOMException) 
      ? `Microphone error: ${errorMessage}`
      : `Failed to access microphone: ${errorMessage}`;
  }

  /**
   * Get specific error message for display media (screen share) failures (DRY implementation)
   */
  private getDisplayMediaErrorMessage(e: unknown): string {
    const errorMessage = this.extractErrorMessage(e);
    
    // Check for custom audio track error first
    if (errorMessage.includes('No audio track')) {
      return errorMessage;
    }
    
    if (e instanceof DOMException && e.name in this.ERROR_MESSAGES.displayMedia) {
      return this.ERROR_MESSAGES.displayMedia[e.name as keyof typeof this.ERROR_MESSAGES.displayMedia];
    }
    
    return (e instanceof DOMException) 
      ? `Screen sharing error: ${errorMessage}`
      : `Could not start Telehealth mode: ${errorMessage}`;
  }

  /**
   * Extract error message from unknown error type
   */
  private extractErrorMessage(e: unknown): string {
    if (e instanceof Error) {
      return e.message;
    }

    if (typeof e === 'string') {
      return e;
    }

    if (typeof e === 'object' && e !== null) {
      if ('error' in e && typeof e.error === 'string') return e.error;
      if ('message' in e && typeof e.message === 'string') return e.message;
      if ('name' in e && typeof e.name === 'string') return e.name;
    }

    return 'An unknown error occurred.';
  }

  /**
   * Reset service state
   */
  reset(): void {
    this.cleanupAllStreams();
    this._audioSource = 'transcribe';
  }

  /**
   * Clean up on service destroy
   */
  ngOnDestroy(): void {
    this.cleanupAllStreams();
    this._screenShareEnded$.complete();
  }
}
